import type {
    LessonProgress
} from "../types/global.js";

import {
    getAccountScopedStorageKey
} from "./learnerStorage.js";

export {
    getAllLessonProgress,
    getLessonProgress,
    markLessonCompleted,
    markSectionCompleted,
    mergeLessonProgress,
    mergeRemoteLessonProgress
};

type LessonContentType =
    | "grammar"
    | "travel";

interface LessonProgressChangeDetail {
    contentType: LessonContentType;
    lessonId: string;
    progress: LessonProgress;
}

interface LessonProgressSnapshot {
    contentType: LessonContentType;
    lessonId: string;
    progress: LessonProgress;
}

const LESSON_PROGRESS_CHANGE_EVENT =
    "dino:lessonprogresschange";
const LESSON_PROGRESS_IMPORTED_EVENT =
    "dino:lessonprogressimported";
const LESSON_PROGRESS_CONTENT_TYPES_STORAGE_KEY =
    "dino_lesson_progress_content_types";

/**
 * Local persistence for lesson progress and user mistakes.
 */

const LESSON_PROGRESS_STORAGE_KEY =
    "dino_lessons_progress";

/* -------------------------------------------------------------------------- */
/* Lesson progress                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Returns the persisted progress state of one lesson.
 */
function getLessonProgress(
    lessonId: string
): LessonProgress {
    const allProgress =
        readLessonProgress();

    return (
        allProgress[
            lessonId
        ]
        ?? {
            status:
                "not_started",

            completedSections:
                [],

            currentSection:
                0,

            lastAccessed:
                null
        }
    );
}

/**
 * Persists one lesson progress record and refreshes its access timestamp.
 */
function saveLessonProgress(
    lessonId: string,
    progress: LessonProgress,
    contentType: LessonContentType
): void {
    const allProgress =
        readLessonProgress();

    const updatedProgress:
        LessonProgress = {
            ...progress,

            completedSections: [
                ...progress.completedSections
            ],

            lastAccessed:
                new Date()
                    .toISOString()
        };

    allProgress[
        lessonId
    ] = updatedProgress;

    localStorage.setItem(
        getAccountScopedStorageKey(
            LESSON_PROGRESS_STORAGE_KEY
        ),
        JSON.stringify(
            allProgress
        )
    );
    saveLessonContentType(
        lessonId,
        contentType
    );

    dispatchProgressChange({
        contentType,
        lessonId,
        progress: updatedProgress
    });
}

/**
 * Marks one section as completed for a lesson.
 *
 * Section completion leaves the lesson in `in_progress` until the feature
 * explicitly confirms that every required section is complete through
 * `markLessonCompleted()`.
 */
function markSectionCompleted(
    lessonId: string,
    sectionId: string,
    contentType: LessonContentType
): void {
    const progress =
        getLessonProgress(
            lessonId
        );

    const completedSections =
        progress.completedSections
            .includes(
                sectionId
            )
            ? [
                ...progress.completedSections
            ]
            : [
                ...progress.completedSections,
                sectionId
            ];

    saveLessonProgress(
        lessonId,
        {
            ...progress,

            completedSections,

            status:
                progress.status === "completed"
                    ? "completed"
                    : "in_progress"
        },
        contentType
    );
}

/**
 * Marks the whole lesson as completed.
 *
 * Grammar and Travel can now use the same persisted lesson-progress contract
 * instead of only marking their final section.
 */
function markLessonCompleted(
    lessonId: string,
    contentType: LessonContentType
): void {
    const progress =
        getLessonProgress(
            lessonId
        );

    saveLessonProgress(
        lessonId,
        {
            ...progress,

            status:
                "completed"
        },
        contentType
    );
}

/** Returns a defensive snapshot used during authenticated bootstrap sync. */
function getAllLessonProgress():
    LessonProgressSnapshot[] {
    const contentTypes = readStringMap(
        getAccountScopedStorageKey(
            LESSON_PROGRESS_CONTENT_TYPES_STORAGE_KEY
        )
    );

    return Object.entries(readLessonProgress()).map(
        ([lessonId, progress]) => ({
            contentType: resolveLessonContentType(
                lessonId,
                contentTypes[lessonId]
            ),
            lessonId,
            progress: {
                ...progress,
                completedSections: [
                    ...progress.completedSections
                ]
            }
        })
    );
}

/** Imports server progress without emitting another outbound sync event. */
function mergeRemoteLessonProgress(
    contentType: LessonContentType,
    lessonId: string,
    remote: LessonProgress
): LessonProgress {
    const allProgress = readLessonProgress();
    const local = getLessonProgress(lessonId);
    const merged = mergeLessonProgress(
        local,
        remote
    );

    allProgress[lessonId] = merged;
    localStorage.setItem(
        getAccountScopedStorageKey(
            LESSON_PROGRESS_STORAGE_KEY
        ),
        JSON.stringify(allProgress)
    );
    saveLessonContentType(
        lessonId,
        contentType
    );

    if (/^(A1|A2|B1|B2|C1)-G-/u.test(lessonId)) {
        const grammarProgress = readStringMap(
            getAccountScopedStorageKey("dino_progress")
        );
        grammarProgress[lessonId] = merged.status;
        localStorage.setItem(
            getAccountScopedStorageKey("dino_progress"),
            JSON.stringify(grammarProgress)
        );
    }

    dispatchLessonProgressEvent(
        LESSON_PROGRESS_IMPORTED_EVENT,
        {
            contentType,
            lessonId,
            progress: merged
        }
    );

    return merged;
}

/** Combines two devices without ever losing completed work. */
function mergeLessonProgress(
    local: LessonProgress,
    remote: LessonProgress
): LessonProgress {
    return {
        status:
            progressRank(remote.status) > progressRank(local.status)
                ? remote.status
                : local.status,
        completedSections: [
            ...new Set([
                ...local.completedSections,
                ...remote.completedSections
            ])
        ],
        currentSection: Math.max(
            local.currentSection,
            remote.currentSection
        ),
        lastAccessed:
            [local.lastAccessed, remote.lastAccessed]
                .filter((value): value is string => Boolean(value))
                .sort()
                .at(-1)
            ?? null
    };
}

function progressRank(
    status: LessonProgress["status"]
): number {
    return status === "completed"
        ? 2
        : status === "in_progress"
            ? 1
            : 0;
}

function dispatchProgressChange(
    detail: LessonProgressChangeDetail
): void {
    dispatchLessonProgressEvent(
        LESSON_PROGRESS_CHANGE_EVENT,
        detail
    );
}

function dispatchLessonProgressEvent(
    eventName: string,
    detail: LessonProgressChangeDetail
): void {
    if (
        typeof window === "undefined"
        || typeof CustomEvent === "undefined"
    ) {
        return;
    }

    window.dispatchEvent(
        new CustomEvent<LessonProgressChangeDetail>(
            eventName,
            { detail }
        )
    );
}

function saveLessonContentType(
    lessonId: string,
    contentType: LessonContentType
): void {
    const contentTypes = readStringMap(
        getAccountScopedStorageKey(
            LESSON_PROGRESS_CONTENT_TYPES_STORAGE_KEY
        )
    );
    contentTypes[lessonId] = contentType;
    localStorage.setItem(
        getAccountScopedStorageKey(
            LESSON_PROGRESS_CONTENT_TYPES_STORAGE_KEY
        ),
        JSON.stringify(contentTypes)
    );
}

function resolveLessonContentType(
    lessonId: string,
    storedContentType: string | undefined
): LessonContentType {
    if (
        storedContentType === "grammar"
        || storedContentType === "travel"
    ) {
        return storedContentType;
    }

    return /^(A1|A2|B1|B2|C1)-G-/u.test(lessonId)
        ? "grammar"
        : "travel";
}

function readStringMap(
    key: string
): Record<string, string> {
    try {
        const parsed = JSON.parse(
            localStorage.getItem(key) || "{}"
        ) as unknown;

        return parsed && typeof parsed === "object" && !Array.isArray(parsed)
            ? parsed as Record<string, string>
            : {};
    } catch {
        return {};
    }
}

/* -------------------------------------------------------------------------- */
/* Persistence readers                                                         */
/* -------------------------------------------------------------------------- */

function readLessonProgress():
    Record<
        string,
        LessonProgress
    > {
    const raw =
        localStorage.getItem(
            getAccountScopedStorageKey(
                LESSON_PROGRESS_STORAGE_KEY
            )
        );

    if (!raw) {
        return {};
    }

    try {
        const parsed =
            JSON.parse(
                raw
            ) as unknown;

        if (
            !parsed
            || typeof parsed
                !== "object"
            || Array.isArray(
                parsed
            )
        ) {
            return {};
        }

        return parsed as Record<
            string,
            LessonProgress
        >;
    } catch {
        return {};
    }
}

export {
    LESSON_PROGRESS_CHANGE_EVENT,
    LESSON_PROGRESS_IMPORTED_EVENT
};

export type {
    LessonContentType,
    LessonProgressChangeDetail,
    LessonProgressSnapshot
};
